/**
 * Converts Ad Studio SVG into absolute-positioned editor HTML.
 *
 * Rects, images, compatible text, groups, gradients, filters, and `<use>` are
 * translated to HTML/CSS. Primitives without a faithful editable equivalent
 * remain inline SVG. Source ids become `data-ht-id` so animation bindings
 * survive conversion; `skipped` reports preserved or lossy features to the
 * caller. Percentage geometry is resolved against the source viewport.
 */

import * as cheerio from "cheerio";
import type { CheerioAPI } from "cheerio";
import { HT_ID_ATTR } from "../html-editor/ids";
import {
  combineColorWithOpacity,
  type DomElement,
  type DomNode,
  fmt,
  isElement,
  num,
  readAttrOrStyle,
  readStyleProp,
} from "./dom";
import { collectFilters, filterCssFor, type FilterMap } from "./filters";

const DROP_TAGS = new Set([
  "defs",
  "style",
  "script",
  "title",
  "desc",
  "metadata",
  // Definitions are re-inlined only when a preserved SVG references them.
  "filter",
]);
/** Primitives preserved as inline SVG rather than editable HTML children. */
const PRESERVED_SVG_TAGS = new Set([
  "circle",
  "ellipse",
  "line",
  "polygon",
  "polyline",
  "path",
  "symbol",
  // Convertible text is handled before this fallback.
  "tspan",
  "clipPath",
  "mask",
  "pattern",
]);

const DEFAULT_WIDTH = 800;
const DEFAULT_HEIGHT = 600;
const TRANSLATE_PATTERN =
  /translate\(\s*(-?[\d.]+)(?:\s*[\s,]\s*(-?[\d.]+))?\s*\)/;
const TRANSLATE_ONLY_PATTERN =
  /^\s*(?:translate\(\s*-?[\d.]+(?:\s*[\s,]\s*-?[\d.]+)?\s*\)\s*)+$/;
const SVG_NS = "http://www.w3.org/2000/svg";
const XHTML_NS = "http://www.w3.org/1999/xhtml";
/** Recursion guard for cyclic `<use>` references. */
const MAX_USE_DEPTH = 8;

interface GradientStop {
  offset: number;
  color: string;
}

interface ConvertContext {
  $: CheerioAPI;
  layers: string[];
  skipped: Set<string>;
  /** SVG `<linearGradient>`/`<radialGradient>` ids → CSS gradient string. */
  gradients: Map<string, string>;
  /** SVG `<filter>` ids → CSS `filter` value, or null when none reproduces it. */
  filters: FilterMap;
  /** Any element with an `id` (anywhere in the tree), keyed for `<use>` lookup. */
  idMap: Map<string, DomElement>;
  /**
   * Accumulated compensation offset applied by leaf emitters (`x +
   * ctx.offsetX`). The frame rule is deliberately dumb: wrappers emit at
   * their raw local coordinates and never absorb this offset — only leaves
   * apply it. It is pushed by the few sites that re-express content relative
   * to a wrapper origin (`applyElementClip`, clipped groups, `<use>` x/y);
   * everything is a pure translate, so applying it once at the leaf is exact.
   */
  offsetX: number;
  offsetY: number;
  /** SVG viewport dimensions, used to resolve `%` positions to pixels. */
  viewportW: number;
  viewportH: number;
  /** Recursion depth for `<use>` resolution. */
  useDepth: number;
  /** Source `id`s already emitted, so `data-ht-id` stays unique per document. */
  usedIds: Set<string>;
  /** Namespace prefixes whose elements become ordinary HTML in foreignObject. */
  xhtmlPrefixes: ReadonlySet<string>;
}

export interface SvgToHtmlResult {
  /** Editor-ready root `<div>`. */
  html: string;
  /** Preserved, dropped, or approximated SVG features. */
  skipped: string[];
}

const INHERITABLE_GROUP_ATTRS = [
  "fill",
  "fill-opacity",
  "stroke",
  "font-family",
  "font-size",
  "text-anchor",
] as const;

/** `skipped` entries that change rendering rather than only editability. */
export const RENDERING_AFFECTING_SKIPS: ReadonlySet<string> = new Set([
  "mask",
  "filter",
  "clip-path",
  "use",
  "transform",
  "gradientUnits",
  "gradientTransform",
  "radialGradient",
  ...INHERITABLE_GROUP_ATTRS,
]);

export interface ConvertSvgToHtmlOptions {
  /**
   * Scale the converted geometry into this box — the same width-fit mapping
   * the browser applies when a viewBox-only `<svg>` stretches across a
   * container (content anchored top-left, overflow clipped). Legacy overlays
   * are sometimes authored in their source ad's coordinate space (e.g. a
   * 2048×2048 static canvas) while the composition is 1080×1080; without this
   * the editor mounts — and on the first save persists — markup whose px
   * values are viewBox/composition too large.
   */
  targetSize?: { width: number; height: number };
}

export function convertSvgToHtml(
  svg: string,
  options?: ConvertSvgToHtmlOptions,
): SvgToHtmlResult {
  const $ = cheerio.load(svg, { xml: true });
  const rootEl = $("svg").first()[0] as unknown as DomElement | undefined;
  if (!rootEl || !isElement(rootEl)) {
    throw new Error("convertSvgToHtml: no <svg> root found");
  }
  const { width, height } = readViewport(rootEl);
  const skipped = new Set<string>();
  const idMap = collectIds(rootEl);
  const ctx: ConvertContext = {
    $,
    layers: [],
    skipped,
    gradients: collectGradients($, idMap, skipped),
    filters: collectFilters($),
    idMap,
    offsetX: 0,
    offsetY: 0,
    viewportW: width,
    viewportH: height,
    useDepth: 0,
    usedIds: new Set(),
    xhtmlPrefixes: collectXhtmlPrefixes(rootEl),
  };
  walk(ctx, rootEl.children);
  const target = options?.targetSize;
  const scale = target && width > 0 ? target.width / width : 1;
  const body =
    scale === 1
      ? ctx.layers.join("\n  ")
      : scaleEmittedGeometry(ctx.layers.join("\n  "), scale);
  const rootW = target ? target.width : width;
  const rootH = target ? target.height : height;
  const style =
    `position:relative;width:${fmt(rootW)}px;height:${fmt(rootH)}px;` +
    `overflow:hidden;background:#fff;`;
  const html = `<div style="${style}">\n  ${body}\n</div>`;
  return { html, skipped: [...ctx.skipped].sort() };
}

/**
 * Uniformly scale the px geometry of emitted layers.
 *
 * Every length the converter emits is a `px` value inside a double-quoted
 * `style` attribute (cheerio serialization normalizes quotes), so scaling
 * style attributes covers positions, sizes, typography, radii, borders and
 * CSS transforms — including agent-authored foreignObject content, where
 * every px length scales uniformly by definition. Preserved inline `<svg>`
 * layers scale through their box instead: `viewBox` stays in source units, so
 * only the svg tag's own width/height attributes (and its wrapper style) are
 * touched — never inner coordinates or path data.
 *
 * Scaled values round to whole px: the factor turns authored integers into
 * long fractions (116 × 1080/2048 = 61.171875) that read as noise in the
 * properties panel, and a ≤0.5px shift is invisible at ad scale.
 *
 * ponytail: a px length in a style attribute INSIDE preserved svg content
 * would double-scale (once here, once via its viewBox). Preserved primitives
 * carry unitless presentation attributes in practice; revisit if one shows up.
 */
function scaleEmittedGeometry(html: string, scale: number): string {
  const scaled = (n: string) => String(Math.round(Number(n) * scale));
  const scalePx = (css: string) =>
    css.replace(/(-?\d*\.?\d+)px/g, (_m, n: string) => `${scaled(n)}px`);
  return html
    .replace(
      / style="([^"]*)"/g,
      (_m, css: string) => ` style="${scalePx(css)}"`,
    )
    .replace(/<svg\b[^>]*/g, (tag) =>
      tag.replace(
        / (width|height)="(-?\d*\.?\d+)"/g,
        (_m, attr: string, n: string) => ` ${attr}="${scaled(n)}"`,
      ),
    );
}

/**
 * Carry the source element's `id` onto the emitted layer as `data-ht-id`,
 * which the html-editor parser adopts as the layer id. Overlay animations are
 * keyed by SVG element id, so this is what lets a binding survive conversion.
 *
 * Injected into the outermost tag of already-generated markup rather than
 * threaded through every emit site, because `foreignObject` alone has three
 * shapes it can produce.
 */
function withHtId(html: string, el: DomElement, ctx: ConvertContext): string {
  const id = el.attribs?.id;
  if (!id || ctx.usedIds.has(id)) return html;
  ctx.usedIds.add(id);
  // Replacer function, not a replacement string: ids are asset-authored, and
  // `$&`/`$'`-style sequences in a replacement string would re-expand raw
  // markup after escaping — breaking out of the quoted attribute.
  return html.replace(
    /^(<[a-zA-Z][\w-]*)/,
    (_match, tag: string) => `${tag} ${HT_ID_ATTR}="${escapeAttribute(id)}"`,
  );
}

function rootHasHtId(html: string): boolean {
  const openingTagEnd = html.indexOf(">");
  return (
    openingTagEnd >= 0 &&
    html.slice(0, openingTagEnd).includes(` ${HT_ID_ATTR}=`)
  );
}

function escapeAttribute(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;")
    .replace(/</g, "&lt;");
}

function walk(ctx: ConvertContext, nodes: DomNode[]): void {
  for (const node of nodes) {
    if (!isElement(node)) continue;
    // `visibility="hidden"` and `display="none"` hide the element (and its
    // subtree for display:none) in SVG. Drop them so we don't render content
    // the source intended to be invisible.
    if (isHidden(node)) continue;
    const tag = node.name;
    if (
      !DROP_TAGS.has(tag) &&
      !PRESERVED_SVG_TAGS.has(tag) &&
      requiresOpaqueSvgFallback(node, ctx)
    ) {
      ctx.skipped.add("opaque-svg");
      ctx.layers.push(withHtId(svgFallback(node, ctx), node, ctx));
      continue;
    }
    if (tag === "rect") {
      ctx.layers.push(
        applyElementClip(ctx, node, () =>
          withHtId(rectToDiv(node, ctx), node, ctx),
        ),
      );
    } else if (tag === "image") {
      ctx.layers.push(
        applyElementClip(ctx, node, () =>
          withHtId(imageToImg(node, ctx), node, ctx),
        ),
      );
    } else if (tag === "foreignObject") {
      ctx.layers.push(
        applyElementClip(ctx, node, () =>
          withHtId(foreignObjectToDiv(node, ctx), node, ctx),
        ),
      );
    } else if (tag === "g") {
      walkGroup(ctx, node);
    } else if (tag === "use") {
      walkUse(ctx, node);
    } else if (tag === "svg") {
      walkNestedSvg(ctx, node);
    } else if (tag === "text") {
      const shape = analyzeText(node);
      if (shape !== null) {
        ctx.layers.push(
          applyElementClip(ctx, node, () =>
            withHtId(textToHtml(node, ctx, shape), node, ctx),
          ),
        );
      } else {
        // Unconvertible text renders through the preserved-svg fallback,
        // like the primitives below — visible and animation-addressable,
        // just not an editable text layer.
        ctx.skipped.add(tag);
        ctx.layers.push(withHtId(svgFallback(node, ctx), node, ctx));
      }
    } else if (DROP_TAGS.has(tag)) {
      continue;
    } else if (PRESERVED_SVG_TAGS.has(tag)) {
      ctx.skipped.add(tag);
      // The fallback layer contains exactly this one primitive, so the id
      // still identifies it — an animation bound here animates the right thing
      // even though the layer isn't individually editable.
      ctx.layers.push(withHtId(svgFallback(node, ctx), node, ctx));
    } else if (tag === "a") {
      // SVG <a> wraps content for linking; link semantics don't apply in the
      // editor surface, but children still need to render. Recurse through.
      walk(ctx, node.children);
    } else {
      // Unknown element — recurse into children so we don't drop subtrees that
      // happen to be wrapped in a tag we haven't seen before.
      walk(ctx, node.children);
    }
  }
}

/**
 * Effects that cannot be represented by the editable HTML layer model stay
 * together as one inline SVG layer. This keeps the original pixels and all
 * descendant ids intact while making only that layer opaque to the controls
 * panel; the rest of the scene remains editable.
 */
function requiresOpaqueSvgFallback(
  el: DomElement,
  ctx: ConvertContext,
): boolean {
  let required = false;
  const mask = readAttrOrStyle(el, "mask")?.trim();
  if (mask && mask !== "none") {
    ctx.skipped.add("mask");
    required = true;
  }

  const clipPath = readAttrOrStyle(el, "clip-path")?.trim();
  if (
    clipPath &&
    clipPath !== "none" &&
    resolveClipRect(ctx, clipPath) === null
  ) {
    ctx.skipped.add("clip-path");
    required = true;
  }

  const filterSkips = new Set<string>();
  filterCssFor(el, ctx.filters, filterSkips);
  if (filterSkips.has("filter")) {
    ctx.skipped.add("filter");
    required = true;
  }

  if (el.name === "g") {
    for (const attr of INHERITABLE_GROUP_ATTRS) {
      const value = readAttrOrStyle(el, attr);
      if (value !== undefined && !(attr === "fill" && value === "none")) {
        ctx.skipped.add(attr);
        required = true;
      }
    }
  }

  return required;
}

/**
 * Wrap an element's emitted HTML in an `overflow:hidden` wrapper sized to the
 * element's `clip-path` rect. The element's left/top inside the wrapper is
 * shifted by the rect's canvas position so the visible content lines up
 * unchanged. Unhandleable clip refs are recorded in `skipped` and rendered
 * without the clip (so the layer still appears in the editor — just unclipped).
 *
 * Walking pattern: callers temporarily push `(-clipRect.x, -clipRect.y)` into
 * `ctx.offsetX/Y` so the handler emits the element with wrapper-relative
 * coords. We can't post-process the emitted HTML string cheaply.
 */
function applyElementClip(
  ctx: ConvertContext,
  el: DomElement,
  emit: () => string,
): string {
  const clipAttr = el.attribs["clip-path"];
  if (clipAttr === undefined) return emit();
  const clipRect = resolveClipRect(ctx, clipAttr);
  if (clipRect === null) {
    ctx.skipped.add("clip-path");
    return emit();
  }
  // Shift inner offsets so the handler positions the element relative to the
  // clip wrapper instead of the canvas root.
  ctx.offsetX -= clipRect.x;
  ctx.offsetY -= clipRect.y;
  const elementHtml = emit();
  ctx.offsetX += clipRect.x;
  ctx.offsetY += clipRect.y;
  return (
    `<div style="position:absolute;left:${fmt(clipRect.x)}px;top:${fmt(clipRect.y)}px;` +
    `width:${fmt(clipRect.width)}px;height:${fmt(clipRect.height)}px;` +
    `overflow:hidden;">${elementHtml}</div>`
  );
}

function walkGroup(ctx: ConvertContext, group: DomElement): void {
  recordMaskWarning(group, ctx);
  // SVG presentation attributes on a <g> inherit into children; the HTML
  // mapping doesn't thread inheritance, so a child relying on them would
  // render differently. Record the divergence. (`fill="none"` matches the
  // conversion's unfilled default, so it can't diverge.)
  for (const attr of INHERITABLE_GROUP_ATTRS) {
    const value = readAttrOrStyle(group, attr);
    if (value !== undefined && !(attr === "fill" && value === "none")) {
      ctx.skipped.add(attr);
    }
  }
  const transform = group.attribs.transform;
  const clipAttr = group.attribs["clip-path"];
  const clipRect = resolveClipRect(ctx, clipAttr);
  if (clipAttr !== undefined && clipRect === null) {
    // Clip is present but not in a shape we can map (non-rect, multiple shapes,
    // path, …). Record it so callers can warn that the result isn't clipped.
    ctx.skipped.add("clip-path");
  }
  const hasComplexTransform = !!transform && !isPureTranslate(transform);
  const complexTransformCss = hasComplexTransform
    ? svgTransformToCss(transform)
    : null;
  if (hasComplexTransform && complexTransformCss === null) {
    // No faithful CSS equivalent (e.g. an unknown op) — preserve the whole
    // group as inline SVG so it still renders exactly, just not editably.
    // The filter goes untranslated on purpose: the fallback re-inlines the
    // real `<filter>`, so asking for CSS here would only risk recording a
    // divergence that the fallback doesn't actually have.
    ctx.skipped.add("transform");
    ctx.layers.push(withHtId(svgFallback(group, ctx), group, ctx));
    return;
  }

  // Group-wide effects ride the outermost wrapper this group produces, so they
  // apply to the subtree as one — which is what `<g opacity>` / `<g filter>`
  // mean. Their presence also rules out the bare pass-through below.
  const groupEffects = opacityCss(group) + elementFilterCss(group, ctx);

  // Pure-translate (or no) transform, no clip, no group effects: emit a
  // wrapper at the group's translate origin with the children untouched. A
  // 0×0 box is deliberate here and is exactly why `groupEffects` can't take
  // this path — `opacity`/`filter` need a wrapper with real extent. Addressable
  // groups normally keep that wrapper because parent and child ids can be
  // independent animation targets. The single anonymous child exception below
  // transfers a no-op group's identity to that child instead.
  //
  // The mapping is deliberately dumb — SVG children of a translated `<g>` are
  // already in the group's local coordinate system, so the wrapper carries the
  // translate and the children keep their raw coordinates. No measuring, no
  // coordinate rewriting: the wrapper is 0×0 with `overflow:visible`, which
  // renders identically, and the editor derives a group's bounds from its
  // children.
  if (!clipRect && !groupEffects && !hasComplexTransform) {
    const t = parseTranslate(transform);
    const outerLayers = ctx.layers;
    ctx.layers = [];
    walk(ctx, group.children);
    const childLayers = ctx.layers;
    ctx.layers = outerLayers;

    // A no-op group wrapping one anonymous layer is commonly authored only
    // to give a legacy sidecar animation a target id. At the canvas origin,
    // applying that id (and therefore its lifted motion) directly to the sole
    // child is render-equivalent and avoids a zero-sized Group in the editor.
    // Keep the wrapper when the child has its own id: parent and child may be
    // independently targeted even when the parent currently has one child.
    if (
      t.x === 0 &&
      t.y === 0 &&
      childLayers.length === 1 &&
      group.attribs.id &&
      !rootHasHtId(childLayers[0]!)
    ) {
      ctx.layers.push(withHtId(childLayers[0]!, group, ctx));
      return;
    }

    const children = childLayers.join("");
    ctx.layers.push(
      withHtId(
        `<div style="position:absolute;left:${fmt(t.x)}px;top:${fmt(t.y)}px;` +
          `width:0px;height:0px;overflow:visible;">${children}</div>`,
        group,
        ctx,
      ),
    );
    return;
  }

  // Otherwise we need at least one wrapper. Build inside out: transform inner,
  // then clip-rect outer. Group effects ride whichever wrapper is outermost.

  let innerHtml: string;
  // When the outermost wrapper is the clip wrapper, its origin is the clip
  // rect's canvas position; inner content must be expressed relative to that.
  // Otherwise the outermost wrapper sits at canvas (0,0) (effects-only path)
  // or at outerOffset (transform-only path), and content stays canvas-relative.
  const innerOriginX = clipRect ? clipRect.x : 0;
  const innerOriginY = clipRect ? clipRect.y : 0;

  if (hasComplexTransform) {
    // CSS-transform wrapper resets inner offsets to 0 so children's absolute
    // positions become wrapper-local before the transform applies.
    // `transform-origin: 0 0` matches SVG user-space semantics.
    const outerLayers = ctx.layers;
    const outerOffsetX = ctx.offsetX;
    const outerOffsetY = ctx.offsetY;
    ctx.layers = [];
    ctx.offsetX = 0;
    ctx.offsetY = 0;
    walk(ctx, group.children);
    const children = ctx.layers.join("");
    ctx.layers = outerLayers;
    ctx.offsetX = outerOffsetX;
    ctx.offsetY = outerOffsetY;

    const cssTransform = complexTransformCss;
    // The transform wrapper's left/top sits at the (possibly translated) outer
    // offset, but expressed relative to whatever wrapper contains it.
    const twLeft = outerOffsetX - innerOriginX;
    const twTop = outerOffsetY - innerOriginY;
    // Group effects ride the outermost wrapper. With no clip wrapper, the
    // transform wrapper IS the outermost, so they ride on it.
    const transformEffects = clipRect ? "" : groupEffects;
    innerHtml =
      `<div style="position:absolute;left:${fmt(twLeft)}px;top:${fmt(twTop)}px;` +
      `transform:${cssTransform};transform-origin:0 0;overflow:visible;${transformEffects}">${children}</div>`;
  } else {
    // No complex transform — pure-translate children laid out canvas-relative
    // and shifted into the wrapper's local origin.
    const t = parseTranslate(transform);
    const outerLayers = ctx.layers;
    ctx.layers = [];
    ctx.offsetX += t.x - innerOriginX;
    ctx.offsetY += t.y - innerOriginY;
    walk(ctx, group.children);
    ctx.offsetX -= t.x - innerOriginX;
    ctx.offsetY -= t.y - innerOriginY;
    innerHtml = ctx.layers.join("");
    ctx.layers = outerLayers;
  }

  // Effects-only wrapper (no clip, no transform). A canvas-spanning div is
  // sufficient — children keep their canvas-relative coords.
  if (!hasComplexTransform && groupEffects && !clipRect) {
    innerHtml =
      `<div style="position:absolute;left:0;top:0;` +
      `width:${fmt(ctx.viewportW)}px;height:${fmt(ctx.viewportH)}px;` +
      `overflow:visible;${groupEffects}">${innerHtml}</div>`;
  }

  // Outermost wrapper applies the SVG `clip-path` as `overflow:hidden` on a
  // div sized to the clip rect itself (rather than canvas-spanning + CSS
  // `clip-path:inset`). The visual result is identical, but the editor's
  // layer model now has a wrapper whose bounding box matches the visible
  // region — much friendlier when selecting / dragging in the canvas.
  if (clipRect) {
    innerHtml =
      `<div style="position:absolute;left:${fmt(clipRect.x)}px;top:${fmt(clipRect.y)}px;` +
      `width:${fmt(clipRect.width)}px;height:${fmt(clipRect.height)}px;` +
      `overflow:hidden;${groupEffects}">${innerHtml}</div>`;
  }

  ctx.layers.push(withHtId(innerHtml, group, ctx));
}

interface ClipRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Resolve a `clip-path="url(#id)"` reference to a rect in canvas user space
 * when the referenced `<clipPath>` is a single `<rect>` — the common "show
 * only this region" pattern. Returns null for anything we can't safely map
 * (external refs, polygon/path shapes, multiple shapes).
 *
 * Callers wrap the clipped content in an `overflow:hidden` div sized to this
 * rect (vs. a canvas-spanning `clip-path:inset(...)`) so the editor's layer
 * model gets a wrapper whose bounding box matches the visible region.
 */
function resolveClipRect(
  ctx: ConvertContext,
  clipPathAttr: string | undefined,
): ClipRect | null {
  if (!clipPathAttr) return null;
  const urlMatch = /^url\(#([^)]+)\)$/.exec(clipPathAttr.trim());
  if (!urlMatch) return null;
  const target = ctx.idMap.get(urlMatch[1]!);
  if (!target || target.name !== "clipPath") return null;
  const rects: DomElement[] = [];
  for (const child of target.children) {
    if (isElement(child) && child.name === "rect") rects.push(child);
  }
  if (rects.length !== 1) return null;
  const rect = rects[0]!;
  return {
    x: num(rect.attribs.x, ctx.viewportW),
    y: num(rect.attribs.y, ctx.viewportH),
    width: num(rect.attribs.width, ctx.viewportW),
    height: num(rect.attribs.height, ctx.viewportH),
  };
}

function walkUse(ctx: ConvertContext, use: DomElement): void {
  const href = use.attribs.href ?? use.attribs["xlink:href"] ?? "";
  if (!href) {
    ctx.skipped.add("use");
    return;
  }
  if (href.startsWith("#")) {
    resolveInternalUse(ctx, use, href.slice(1));
    return;
  }
  // External SVG references (`ad:logo.svg`, `https://…/logo.svg`) behave
  // visually like images. Treat them as `<img>` so the asset loads through
  // the normal `ad:` resolver and the editor gets a draggable layer.
  const x = num(use.attribs.x, ctx.viewportW);
  const y = num(use.attribs.y, ctx.viewportH);
  // Absent width/height means the referenced asset's intrinsic size — omit
  // the declarations rather than collapsing the image to 0×0.
  const width =
    use.attribs.width !== undefined
      ? `width:${fmt(num(use.attribs.width, ctx.viewportW))}px;`
      : "";
  const height =
    use.attribs.height !== undefined
      ? `height:${fmt(num(use.attribs.height, ctx.viewportH))}px;`
      : "";
  let extras = `object-fit:contain;${IMG_RESET_DEFENSE}`;
  const opacity = use.attribs.opacity;
  if (opacity !== undefined && opacity !== "") {
    extras += `opacity:${fmt(parseOpacity(opacity))};`;
  }
  ctx.layers.push(
    withHtId(
      `<img src="${href}" style="position:absolute;` +
        `left:${fmt(x + ctx.offsetX)}px;top:${fmt(y + ctx.offsetY)}px;` +
        `${width}${height}${extras}">`,
      use,
      ctx,
    ),
  );
}

function resolveInternalUse(
  ctx: ConvertContext,
  use: DomElement,
  refId: string,
): void {
  if (ctx.useDepth >= MAX_USE_DEPTH) {
    ctx.skipped.add("use");
    return;
  }
  const target = ctx.idMap.get(refId);
  if (!target) {
    ctx.skipped.add("use");
    return;
  }

  // SVG `<use>` paints the referenced element with the use's own attributes
  // overlaid: x/y add to the target's position, width/height override, and
  // presentation attributes (fill, opacity, …) cascade in unless the target
  // sets them explicitly. Mirror that with a shallow merge — the target wins
  // for its own positioning attrs (we apply use's x/y as an extra offset),
  // and use wins for presentation attrs the target left unset.
  const merged = mergeUseTarget(target, use);
  const useX = num(use.attribs.x, ctx.viewportW);
  const useY = num(use.attribs.y, ctx.viewportH);
  ctx.useDepth += 1;
  ctx.offsetX += useX;
  ctx.offsetY += useY;
  walk(ctx, [merged]);
  ctx.offsetX -= useX;
  ctx.offsetY -= useY;
  ctx.useDepth -= 1;
}

function mergeUseTarget(target: DomElement, use: DomElement): DomElement {
  const merged: DomElement = {
    type: "tag",
    name: target.name,
    attribs: { ...target.attribs },
    children: target.children,
  };
  // Use's `width` / `height` override the target when present.
  for (const key of ["width", "height"]) {
    const v = use.attribs[key];
    if (v !== undefined && v !== "") merged.attribs[key] = v;
  }
  // Presentation attrs cascade from <use> into the target only when the
  // target hasn't already set them (matches SVG cascade semantics).
  for (const key of ["fill", "stroke", "stroke-width", "opacity"]) {
    if (use.attribs[key] !== undefined && merged.attribs[key] === undefined) {
      merged.attribs[key] = use.attribs[key];
    }
  }
  // Identity belongs to the instance, not the template: a `<defs>` element's
  // id would otherwise be stamped onto every `<use>` that paints it, and the
  // second instance onward would silently lose it to the uniqueness guard.
  if (use.attribs.id !== undefined) merged.attribs.id = use.attribs.id;
  else delete merged.attribs.id;
  return merged;
}

function walkNestedSvg(ctx: ConvertContext, svg: DomElement): void {
  // Nested <svg> is positioned via x/y/width/height on the canvas and brings
  // its own viewBox / coordinate system. Preserve it verbatim inside a
  // positioned wrapper so the inner viewBox math still applies.
  const x = num(svg.attribs.x, ctx.viewportW);
  const y = num(svg.attribs.y, ctx.viewportH);
  const width = num(svg.attribs.width, ctx.viewportW);
  const height = num(svg.attribs.height, ctx.viewportH);
  const inner = serializeElement(ctx, svg);
  const widthPart = width > 0 ? `width:${fmt(width)}px;` : "";
  const heightPart = height > 0 ? `height:${fmt(height)}px;` : "";
  ctx.layers.push(
    withHtId(
      `<div style="position:absolute;` +
        `left:${fmt(x + ctx.offsetX)}px;top:${fmt(y + ctx.offsetY)}px;` +
        `${widthPart}${heightPart}overflow:visible;">${inner}</div>`,
      svg,
      ctx,
    ),
  );
  ctx.skipped.add("svg");
}

/**
 * Wrap an SVG-only primitive (path, circle, text, …) in a canvas-sized inline
 * `<svg>` layer. The element keeps its original SVG-coordinate values and
 * gets the accumulated translate offset injected via a `<g>`. Renders
 * faithfully, but the editor sees a single opaque layer rather than the
 * individual primitive — that's the trade-off for visual parity.
 */
function svgFallback(el: DomElement, ctx: ConvertContext): string {
  const serialized = serializeElement(ctx, el);
  const inner =
    ctx.offsetX !== 0 || ctx.offsetY !== 0
      ? `<g transform="translate(${fmt(ctx.offsetX)} ${fmt(ctx.offsetY)})">${serialized}</g>`
      : serialized;
  // Span the canvas so the primitive's original coords land where the source
  // SVG put them; `overflow:visible` lets paths that paint outside still show.
  return (
    `<svg xmlns="${SVG_NS}" width="${fmt(ctx.viewportW)}" ` +
    `height="${fmt(ctx.viewportH)}" ` +
    `viewBox="0 0 ${fmt(ctx.viewportW)} ${fmt(ctx.viewportH)}" ` +
    `style="position:absolute;left:0;top:0;` +
    `width:${fmt(ctx.viewportW)}px;height:${fmt(ctx.viewportH)}px;` +
    `overflow:visible;display:block;">` +
    referencedDefs(el, ctx) +
    inner +
    `</svg>`
  );
}

/**
 * A `<defs>` carrying every source definition the preserved subtree
 * references, or "" when it references none. References inside definitions
 * are followed recursively (for example a mask that uses a gradient, or a
 * `<use>` whose target uses a filter).
 *
 * The source `<defs>` was dropped on the way in, which leaves the element's
 * `filter` attribute pointing at nothing — and under SVG 1.1 that means it
 * isn't painted at all. Re-inlining the definition removes that risk, and
 * beats translating it: the browser runs the real filter, so even the chains
 * {@link filterCssFor} can't express render exactly.
 *
 * Two fallback layers referencing the same filter each get their own copy, so
 * the id repeats across the document. Harmless: the copies are identical, and
 * `url(#id)` resolves to the first.
 */
function referencedDefs(el: DomElement, ctx: ConvertContext): string {
  const queued = collectReferencedIds(el);
  const seen = new Set<string>();
  const definitions: DomElement[] = [];

  while (queued.length > 0) {
    const id = queued.shift();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    const definition = ctx.idMap.get(id);
    if (!definition) continue;
    definitions.push(definition);
    for (const nestedId of collectReferencedIds(definition)) {
      if (!seen.has(nestedId)) queued.push(nestedId);
    }
  }

  const defs = definitions.map((def) => serializeElement(ctx, def)).join("");
  return defs === "" ? "" : `<defs>${defs}</defs>`;
}

function collectReferencedIds(root: DomElement): string[] {
  const ids = new Set<string>();
  const stack: DomNode[] = [root];
  while (stack.length > 0) {
    const node = stack.pop();
    if (!node || !isElement(node)) continue;
    for (const [name, value] of Object.entries(node.attribs)) {
      const refs = value.matchAll(/url\(\s*['"]?#([^)'"\s]+)['"]?\s*\)/g);
      for (const match of refs) {
        const id = match[1];
        if (id) ids.add(id);
      }
      if ((name === "href" || name === "xlink:href") && value.startsWith("#")) {
        ids.add(value.slice(1));
      }
    }
    for (const child of node.children) stack.push(child);
  }
  return [...ids];
}

function serializeElement(ctx: ConvertContext, el: DomElement): string {
  return ctx.$.html(el as any);
}

function rectToDiv(rect: DomElement, ctx: ConvertContext): string {
  recordMaskWarning(rect, ctx);
  let extras = "";

  // Resolve fill — solid color, gradient ref, or none. When a `fill-opacity`
  // is also set, fold it into the fill color via color-mix (same primitive we
  // use for gradient stops). For gradient refs, fill-opacity is dropped with
  // a warning because applying it would require rewriting each stop.
  const fill = (readAttrOrStyle(rect, "fill") ?? "").trim();
  const background = resolveFill(fill, ctx.gradients);
  if (background) {
    const fillOpacity = readAttrOrStyle(rect, "fill-opacity");
    if (fillOpacity !== undefined && background.startsWith("url(")) {
      // Should not happen — resolveFill maps url(#...) to its gradient CSS,
      // never returns a raw url(). Belt-and-braces.
      ctx.skipped.add("fill-opacity");
      extras += `background:${background};`;
    } else if (
      fillOpacity !== undefined &&
      (background.includes("gradient(") || background.startsWith("color-mix"))
    ) {
      ctx.skipped.add("fill-opacity");
      extras += `background:${background};`;
    } else {
      extras += `background:${combineColorWithOpacity(background, fillOpacity)};`;
    }
  }

  // Corner radii. SVG allows asymmetric `rx`/`ry`; when only one is set, both
  // default to the same value (per spec).
  const rxAttr = readAttrOrStyle(rect, "rx");
  const ryAttr = readAttrOrStyle(rect, "ry");
  if (rxAttr || ryAttr) {
    const rxN = num(rxAttr ?? ryAttr);
    const ryN = num(ryAttr ?? rxAttr);
    extras +=
      rxN === ryN
        ? `border-radius:${fmt(rxN)}px;`
        : `border-radius:${fmt(rxN)}px / ${fmt(ryN)}px;`;
  }

  // SVG stroke is centered on the path; CSS `border` lives outside the box
  // (or inside with border-box). For rect outlines (CTA buttons, dividers,
  // bordered cards) the visual difference is sub-pixel — use border-box so
  // stroked rects keep their declared width/height.
  const stroke = (readAttrOrStyle(rect, "stroke") ?? "").trim();
  if (stroke && stroke !== "none") {
    const strokeWidth = num(readAttrOrStyle(rect, "stroke-width")) || 1;
    const strokeOpacity = readAttrOrStyle(rect, "stroke-opacity");
    const strokeColor = combineColorWithOpacity(stroke, strokeOpacity);
    extras += `border:${fmt(strokeWidth)}px solid ${strokeColor};box-sizing:border-box;`;
  }

  const opacity = readAttrOrStyle(rect, "opacity");
  if (opacity !== undefined && opacity !== "") {
    extras += `opacity:${fmt(parseOpacity(opacity))};`;
  }

  extras += elementTransformCss(rect, ctx);
  extras += elementFilterCss(rect, ctx);
  return `<div style="${styleBox(rect, ctx, extras)}"></div>`;
}

function imageToImg(img: DomElement, ctx: ConvertContext): string {
  recordMaskWarning(img, ctx);
  const preserve = img.attribs.preserveAspectRatio ?? "xMidYMid meet";
  const objectFit = preserve.includes("slice")
    ? "cover"
    : preserve.includes("none")
      ? "fill"
      : "contain";
  const src = img.attribs.href ?? img.attribs["xlink:href"] ?? "";
  let extras = `object-fit:${objectFit};${IMG_RESET_DEFENSE}`;
  const opacity = readAttrOrStyle(img, "opacity");
  if (opacity !== undefined && opacity !== "") {
    extras += `opacity:${fmt(parseOpacity(opacity))};`;
  }
  extras += elementTransformCss(img, ctx);
  extras += elementFilterCss(img, ctx);
  return `<img src="${src}" style="${styleBox(img, ctx, extras)}">`;
}

/**
 * Many host pages (including ours, via `global-styles.tsx`) apply a CSS reset
 * `img { max-width: 100%; height: auto }` to constrain content imagery to its
 * container. SVG-authored images intentionally exceed the canvas width (e.g.
 * a hero image at width=1736 positioned at left=-475 to bleed off both sides
 * of a 1080-wide canvas, then clipped by a `clip-path` rect). The reset
 * silently clamps those widths to the parent, collapsing the bleed and
 * showing a half-width image. Neutralize the reset on every converted `<img>`.
 */
const IMG_RESET_DEFENSE = "max-width:none;max-height:none;";

function foreignObjectToDiv(fo: DomElement, ctx: ConvertContext): string {
  recordMaskWarning(fo, ctx);
  // Trim surrounding whitespace: SVG source often pretty-prints foreignObject
  // contents with newlines/indentation, which survive as text nodes in HTML
  // block-flow and push the inner element down by a default line-height —
  // shifting text out of its intended box (and out of CTA buttons).
  const inner = (ctx.$(fo as any).html() ?? "").trim();
  const normalized = trimSoleTextNodeWhitespace(
    normalizeForeignContent(inner, ctx.xhtmlPrefixes),
  );

  const t = pureTranslateOf(fo.attribs.transform);
  const wrapperLeft = num(fo.attribs.x, ctx.viewportW) + ctx.offsetX + t.x;
  const wrapperTop = num(fo.attribs.y, ctx.viewportH) + ctx.offsetY + t.y;
  const wrapperWidth = num(fo.attribs.width, ctx.viewportW);

  // A non-translate transform precludes the unstack: it would require
  // composing the transform with each line's already-absolute position, which
  // CSS won't let us express cleanly. Emit the wrapper.
  //
  // A filter precludes it for a different reason: SVG applies the effect to
  // the element as one group, and re-applying it per line would double it up
  // wherever the lines' ink overlaps. The wrapper keeps it a single group.
  const transformDecl = elementTransformCss(fo, ctx);
  const filterDecl = elementFilterCss(fo, ctx);
  if (!transformDecl && !filterDecl) {
    // Vertical stack of unpositioned text-only divs — common foreignObject
    // pattern for multi-line headlines. The editor's HTML parser (`parse.ts`)
    // runs against a detached DOMParser document and reads width/height from
    // inline style only, so a `<div>line</div><div>line</div>` block stack
    // (which renders fine via real browser layout) collapses to width:0;
    // height:0 layers there. Unstack into one absolute-positioned layer per
    // line with height computed from font-size × line-height.
    const unstacked = tryUnstackForeignTextLines(
      normalized,
      wrapperLeft,
      wrapperTop,
      wrapperWidth,
    );
    if (unstacked !== null) return unstacked;
  }

  return `<div style="${styleBox(fo, ctx, transformDecl + filterDecl)}">${normalized}</div>`;
}

/**
 * Inline HTML tags allowed inside a foreignObject text line. Block-level
 * descendants (nested `<div>`/`<p>`/…) disqualify the unstack — those need
 * the wrapper-and-recurse path to keep layout fidelity.
 */
const FOREIGN_TEXT_INLINE_TAGS: ReadonlySet<string> = new Set([
  "span",
  "a",
  "em",
  "strong",
  "i",
  "b",
  "u",
  "s",
  "mark",
  "code",
  "sub",
  "sup",
  "small",
  "br",
  "wbr",
  "kbd",
  "samp",
  "var",
  "time",
  "abbr",
  "cite",
  "dfn",
  "q",
  "label",
  "del",
  "ins",
]);

/**
 * Detect `<wrapper><div>line1</div><div>line2</div>…</wrapper>` (or the same
 * pattern flat without the wrapper) inside a foreignObject and emit one
 * absolute-positioned layer per line. Each line's height is computed from its
 * own `font-size` × `line-height` (defaulting to 1.2 when the line-height is
 * missing/normal) so different per-line font sizes stack correctly.
 *
 * Returns null when the inner doesn't match the stack pattern — the caller
 * falls back to the existing wrap-in-a-positioned-div path.
 */
function tryUnstackForeignTextLines(
  inner: string,
  wrapperLeft: number,
  wrapperTop: number,
  wrapperWidth: number,
): string | null {
  if (!inner.trim()) return null;
  const $ = cheerio.load(inner, null, false);
  const significant = $.root()
    .contents()
    .filter((_, n) => {
      if (n.type === "tag") return true;
      if (n.type === "text") return !/^\s*$/.test(n.data ?? "");
      return false;
    });

  // The foreignObject's body is usually a single `<div xmlns="http://www.w3.org/1999/xhtml">`
  // wrapper around the line divs. Accept that shape, or a flat list of line
  // divs at the root.
  let lineNodes: DomElement[];
  if (significant.length === 1) {
    const root = significant.first();
    const rootNode = root[0];
    if (!rootNode || rootNode.type !== "tag" || rootNode.name !== "div") {
      return null;
    }
    // A styled root is a single text leaf, not a transparent wrapper; let it
    // fall through to the existing wrap-in-div path so `parse.ts`'s
    // `tryFlattenTextWrapper` can promote it to a TextLayer.
    if ((root.attr("style") ?? "").trim() !== "") return null;
    lineNodes = root
      .children()
      .toArray()
      .filter((n) => n.type === "tag") as DomElement[];
  } else if (significant.length > 1) {
    const tags = significant.toArray().filter((n) => n.type === "tag");
    if (tags.length !== significant.length) return null;
    lineNodes = tags as DomElement[];
  } else {
    return null;
  }

  // A single line goes through `tryFlattenTextWrapper` in `parse.ts` and ends
  // up with the foreignObject's geometry — no benefit to unstacking here.
  if (lineNodes.length < 2) return null;

  interface Line {
    html: string;
    style: string;
    lineHeightPx: number;
  }
  const lines: Line[] = [];
  for (const node of lineNodes) {
    if (node.name !== "div") return null;
    for (const grand of node.children ?? []) {
      if (
        grand.type === "tag" &&
        !FOREIGN_TEXT_INLINE_TAGS.has((grand as DomElement).name.toLowerCase())
      ) {
        return null;
      }
    }
    const ch = $(node as any);
    const style = ch.attr("style") ?? "";
    const fontSize = readPx(style, "font-size");
    if (fontSize === null || fontSize <= 0) return null;
    lines.push({
      html: ch.html() ?? "",
      style,
      lineHeightPx: resolveLineHeightPx(style, fontSize),
    });
  }

  const parts: string[] = [];
  let topOffset = 0;
  for (const line of lines) {
    const positioning =
      `position:absolute;` +
      `left:${fmt(wrapperLeft)}px;` +
      `top:${fmt(wrapperTop + topOffset)}px;` +
      `width:${fmt(wrapperWidth)}px;` +
      `height:${fmt(line.lineHeightPx)}px;`;
    const rest = stripStyleProps(line.style, POSITION_BOX_PROPS);
    const combinedStyle = positioning + (rest ? `${rest};` : "");
    parts.push(`<div style="${combinedStyle}">${line.html}</div>`);
    topOffset += line.lineHeightPx;
  }
  return parts.join("");
}

/**
 * Resolve a CSS `line-height` declaration to a pixel value, falling back to
 * `fontSize * 1.2` when the value is missing, `normal`, or in a unit we don't
 * handle. Accepts unitless multipliers (the spec-recommended form) and px
 * values; em/%/calc and other forms drop back to the default.
 */
function resolveLineHeightPx(style: string, fontSize: number): number {
  const re = /(?:^|;)\s*line-height\s*:\s*([^;]+)/i;
  const m = re.exec(style);
  if (!m) return fontSize * 1.2;
  const value = (m[1] ?? "").trim();
  if (!value || value === "normal") return fontSize * 1.2;
  if (/^-?\d+(?:\.\d+)?$/.test(value)) return fontSize * Number(value);
  const px = /^(-?\d+(?:\.\d+)?)\s*px$/i.exec(value);
  if (px) return Number(px[1]);
  return fontSize * 1.2;
}

function stripStyleProps(style: string, props: readonly string[]): string {
  let out = style;
  for (const prop of props) {
    out = out.replace(new RegExp(`(?:^|;)\\s*${prop}\\s*:[^;]*;?`, "gi"), ";");
  }
  return out
    .replace(/;{2,}/g, ";")
    .replace(/^[\s;]+/, "")
    .replace(/[\s;]+$/, "");
}

const STYLE_HAS_POSITION = /(?:^|;)\s*position\s*:/i;
const STYLE_HAS_OFFSET = /(?:^|;)\s*(?:left|top|right|bottom)\s*:/i;
/** Props re-emitted by the unstack, stripped from a line's authored style. */
const POSITION_BOX_PROPS = ["position", "left", "top", "width", "height"];

/**
 * Foreign-object inner HTML is preserved verbatim from the source SVG. Some
 * sources (e.g. design tools that author SVG with embedded HTML) emit elements
 * with `left:` / `top:` / `right:` / `bottom:` in `style` but never declare a
 * `position` value — those offsets are silently ignored by the browser, and
 * the editor's drag/resize math then operates on phantom coordinates. Promote
 * any such element to `position: absolute` so the rendered geometry matches
 * the inline style.
 */
/**
 * Pretty-printed SVG sources often indent text inside a wrapper div:
 *   <div style="display:flex;justify-content:center;">
 *       SHOP THE BOOT
 *   </div>
 * Those leading/trailing newlines + indent are real text nodes in HTML
 * block-flow, and shift flex-centered text off-center. `parse.ts` separately
 * trims a text leaf's content on the way into the editor model, which is what
 * keeps indentation from painting as a blank first line while editing.
 *
 * Trim leading/trailing whitespace from text nodes that are the SOLE child
 * of an element. We can't safely trim a text node that lives between inline
 * siblings (e.g. `<span>A</span> <span>B</span>`) — removing the inter-span
 * whitespace would glue the spans together. Sole-child text nodes have no
 * such adjacency, so trimming is always safe.
 */
function trimSoleTextNodeWhitespace(inner: string): string {
  // Cheap early-out: no leading/trailing whitespace inside any element means
  // there's nothing to trim. Skips the cheerio round-trip for the common case.
  if (!/>\s|\s</.test(inner)) return inner;
  const $ = cheerio.load(inner, null, false);
  $("*").each((_, el) => {
    if (el.type !== "tag") return;
    if (el.children.length !== 1) return;
    const child = el.children[0];
    if (!child || child.type !== "text") return;
    const text = child.data ?? "";
    const trimmed = text.replace(/^\s+|\s+$/g, "");
    if (trimmed !== text) child.data = trimmed;
  });
  return $.html();
}

function normalizeForeignContent(
  inner: string,
  xhtmlPrefixes: ReadonlySet<string>,
): string {
  if (!inner) return inner;
  // foreignObject content is frequently authored with an explicit namespace
  // prefix (`<xhtml:div>`). That prefix is meaningful in the source XML, but
  // leaving it in the emitted HTML creates custom elements rather than real
  // div/span elements. The editor then sees a group hierarchy and discards
  // meaningful direct text nodes around inline spans. Only strip prefixes
  // declared for the XHTML namespace; other namespaced content stays opaque.
  let normalized = inner;
  for (const prefix of xhtmlPrefixes) {
    normalized = normalized
      .replaceAll(`<${prefix}:`, "<")
      .replaceAll(`</${prefix}:`, "</");
  }
  // Skip the cheerio round-trip when nothing in the markup looks like an
  // inline-positioning declaration.
  if (!STYLE_HAS_OFFSET.test(normalized)) return normalized;
  const $ = cheerio.load(normalized, null, false);
  $("[style]").each((_, el) => {
    const style = $(el).attr("style") ?? "";
    if (!STYLE_HAS_OFFSET.test(style)) return;
    if (STYLE_HAS_POSITION.test(style)) return;
    const trimmed = style.trim().replace(/;?\s*$/, "");
    $(el).attr("style", `position:absolute;${trimmed ? `${trimmed};` : ""}`);
  });
  return $.html();
}

function collectXhtmlPrefixes(root: DomElement): ReadonlySet<string> {
  const prefixes = new Set<string>();
  const visit = (el: DomElement): void => {
    for (const [name, value] of Object.entries(el.attribs)) {
      if (name.startsWith("xmlns:") && value === XHTML_NS) {
        prefixes.add(name.slice("xmlns:".length));
      }
    }
    for (const child of el.children) {
      if (isElement(child)) visit(child);
    }
  };
  visit(root);
  return prefixes;
}

/**
 * Fraction of the font-size between a text box's top and the first baseline
 * when `line-height` equals the font-size. Browsers derive it from font
 * ascent/descent metrics; for the Latin web fonts in this corpus the ratio
 * clusters tightly (Arial ≈ 0.81, Roboto ≈ 0.78, Inter ≈ 0.80), so a single
 * constant lands within ~±0.03·font-size — ≈2px on a 72px headline. SVG
 * positions text by baseline; CSS positions the box, so this is the bridge.
 */
const TEXT_ASCENT_RATIO = 0.8;

interface SvgTextLine {
  /** Escaped inner HTML: text plus inline `<span>`s from styled tspans. */
  html: string;
  /** Per-line `dy` advance when the line tspan carries one. */
  dy: number | null;
  /** Per-line absolute `y` when the line tspan carries one. */
  y: number | null;
}

interface SvgTextShape {
  lines: SvgTextLine[];
  /** Uniform baseline-to-baseline advance (px); null for single-line text. */
  advance: number | null;
  /**
   * Horizontal anchor: the text's own `x`, or — the common legacy multi-line
   * pattern — the shared `x` its line tspans carry instead. Undefined means 0.
   */
  anchorX: string | undefined;
}

/**
 * The shape of a `<text>` that can become one editable text layer, or null.
 * Anything rejected here renders through `svgFallback` instead (still
 * visible, just not a discrete text layer) — never silently dropped.
 * Deliberately conservative: a wrong box is worse than an uneditable one,
 * and an id-bearing headline split across several layers would orphan its
 * animation binding.
 */
function analyzeText(el: DomElement): SvgTextShape | null {
  // Per-glyph layout, path text and preserved whitespace have no clean
  // box-model analogue.
  if (
    el.attribs.textLength !== undefined ||
    el.attribs.rotate !== undefined ||
    el.attribs.dx !== undefined ||
    el.attribs["xml:space"] === "preserve"
  ) {
    return null;
  }
  if (isNumberList(el.attribs.x) || isNumberList(el.attribs.y)) return null;
  const fill = readAttrOrStyle(el, "fill");
  // Gradient text needs background-clip:text, which the editor model doesn't
  // represent; invisible text is not worth a layer.
  if (fill !== undefined && (fill.startsWith("url(") || fill === "none")) {
    return null;
  }
  // Gradient strokes have no -webkit-text-stroke analogue either.
  if (readAttrOrStyle(el, "stroke")?.startsWith("url(")) return null;
  const transform = el.attribs.transform;
  if (transform && !isPureTranslate(transform)) return null;

  const lineTspans: DomElement[] = [];
  let inlineHtml = "";
  let hasInlineContent = false;
  for (const child of el.children) {
    if (child.type === "text") {
      const data = textNodeData(child);
      if (data.trim() !== "") {
        // Direct text mixed with line tspans has no defined line to join.
        if (lineTspans.length > 0) return null;
        hasInlineContent = true;
      }
      inlineHtml += escapeHtml(collapseWhitespace(data));
      continue;
    }
    if (!isElement(child)) continue;
    if (child.name !== "tspan") return null;
    if (isLineTspan(child)) {
      if (hasInlineContent) return null;
      lineTspans.push(child);
    } else {
      if (lineTspans.length > 0) return null;
      const span = inlineTspanToSpan(child);
      if (span === null) return null;
      inlineHtml += span;
      hasInlineContent = true;
    }
  }

  if (lineTspans.length === 0) {
    const html = inlineHtml.trim();
    if (html === "") return null;
    return {
      lines: [{ html, dy: null, y: null }],
      advance: null,
      anchorX: el.attribs.x,
    };
  }

  // Legacy multi-line text often carries `x` per line tspan instead of on the
  // `<text>` itself; the first line's x becomes the anchor in that case.
  let anchorX = el.attribs.x;
  const lines: SvgTextLine[] = [];
  for (const tspan of lineTspans) {
    if (
      tspan.attribs.dx !== undefined ||
      tspan.attribs.rotate !== undefined ||
      tspan.attribs.textLength !== undefined ||
      isNumberList(tspan.attribs.x) ||
      isNumberList(tspan.attribs.y)
    ) {
      return null;
    }
    // A per-line font-size would invalidate the shared baseline math.
    if (readAttrOrStyle(tspan, "font-size") !== undefined) return null;
    // Every line must share the same horizontal anchor — otherwise the
    // block can't be one box with one text-align.
    if (tspan.attribs.x !== undefined) {
      if (anchorX === undefined) anchorX = tspan.attribs.x;
      else if (num(tspan.attribs.x) !== num(anchorX)) return null;
    }
    const html = lineInnerHtml(tspan);
    if (html === null) return null;
    // The line tspan's own styling (fill, weight, italics) applies to the
    // whole line; carry it the same way inline tspans carry theirs.
    const styles = inlineStyleDeltas(tspan);
    lines.push({
      html: styles.length > 0 ? wrapInStyledSpan(html, styles) : html,
      dy: tspan.attribs.dy !== undefined ? num(tspan.attribs.dy) : null,
      y: tspan.attribs.y !== undefined ? num(tspan.attribs.y) : null,
    });
  }

  if (lines.length === 1) {
    return { lines, advance: null, anchorX };
  }

  // Uniform advance across lines 2..n, from `dy` or from absolute `y` deltas.
  let advance: number | null = null;
  for (let i = 1; i < lines.length; i += 1) {
    const prev = lines[i - 1]!;
    const line = lines[i]!;
    let step: number;
    if (line.dy !== null) {
      step = line.dy;
    } else if (line.y !== null && prev.y !== null) {
      step = line.y - prev.y;
    } else {
      return null;
    }
    if (!(step > 0)) return null;
    if (advance === null) advance = step;
    else if (Math.abs(step - advance) > 0.01) return null;
  }
  return { lines, advance, anchorX };
}

/** A tspan that starts a new line (carries its own position) vs. inline styling. */
function isLineTspan(tspan: DomElement): boolean {
  return (
    tspan.attribs.x !== undefined ||
    tspan.attribs.y !== undefined ||
    tspan.attribs.dy !== undefined
  );
}

/** Inner HTML of a line tspan: escaped text plus inline styled spans. */
function lineInnerHtml(tspan: DomElement): string | null {
  let html = "";
  for (const child of tspan.children) {
    if (child.type === "text") {
      html += escapeHtml(collapseWhitespace(textNodeData(child)));
      continue;
    }
    if (!isElement(child)) continue;
    if (child.name !== "tspan" || isLineTspan(child)) return null;
    const span = inlineTspanToSpan(child);
    if (span === null) return null;
    html += span;
  }
  return html.trim();
}

/**
 * An inline (non-positioning) tspan becomes a `<span>` carrying its styling
 * deltas — the html editor treats span-styled runs as rich text content.
 */
function inlineTspanToSpan(tspan: DomElement): string | null {
  let inner = "";
  for (const child of tspan.children) {
    if (child.type === "text") {
      inner += escapeHtml(collapseWhitespace(textNodeData(child)));
      continue;
    }
    if (!isElement(child)) continue;
    // Nested positioning tspans inside an inline run bail the whole text.
    if (child.name !== "tspan" || isLineTspan(child)) return null;
    const nested = inlineTspanToSpan(child);
    if (nested === null) return null;
    inner += nested;
  }
  const styles = inlineStyleDeltas(tspan);
  const size = readAttrOrStyle(tspan, "font-size");
  if (size) styles.push(`font-size:${fmt(num(size))}px`);
  return styles.length > 0 ? wrapInStyledSpan(inner, styles) : inner;
}

/** A tspan's styling deltas (fill, weight, italics) as CSS declarations. */
function inlineStyleDeltas(tspan: DomElement): string[] {
  const styles: string[] = [];
  const fill = readAttrOrStyle(tspan, "fill");
  if (fill && fill !== "none" && !fill.startsWith("url(")) {
    styles.push(`color:${fill}`);
  }
  const weight = readAttrOrStyle(tspan, "font-weight");
  if (weight) styles.push(`font-weight:${weight}`);
  if (readAttrOrStyle(tspan, "font-style") === "italic") {
    styles.push("font-style:italic");
  }
  return styles;
}

function wrapInStyledSpan(inner: string, styles: string[]): string {
  return `<span style="${escapeAttribute(`${styles.join(";")};`)}">${inner}</span>`;
}

/**
 * Convert `<text>` into the editor's auto-width text layer: a box that hugs
 * its content (`width:max-content; white-space:nowrap`) anchored by CSS
 * `translateX` — the exact semantic of SVG `text-anchor`, which is what
 * makes the mapping exact horizontally with no width guessing. Vertically,
 * SVG positions the first BASELINE at `y` while CSS positions the box top;
 * {@link TEXT_ASCENT_RATIO} bridges the two.
 */
function textToHtml(
  el: DomElement,
  ctx: ConvertContext,
  shape: SvgTextShape,
): string {
  recordMaskWarning(el, ctx);
  const fontSize = num(readAttrOrStyle(el, "font-size")) || 16;
  const t = pureTranslateOf(el.attribs.transform);
  const left = num(shape.anchorX, ctx.viewportW) + ctx.offsetX + t.x;
  const baseY = num(el.attribs.y, ctx.viewportH) + ctx.offsetY + t.y;

  // Line box height. Single-line hugs the font (L = font-size) so the
  // baseline formula degenerates to `top = y − ascent`.
  const lineHeight = shape.advance ?? fontSize;
  const first = shape.lines[0]!;
  const firstBaseline =
    first.y !== null ? first.y + ctx.offsetY + t.y : baseY + (first.dy ?? 0);

  const dominant = readAttrOrStyle(el, "dominant-baseline");
  let top: number;
  if (dominant === "middle" || dominant === "central") {
    top = firstBaseline - lineHeight / 2;
  } else if (dominant === "hanging" || dominant === "text-before-edge") {
    top = firstBaseline;
  } else {
    // Alphabetic baseline: half-leading above the ascent.
    top =
      firstBaseline -
      ((lineHeight - fontSize) / 2 + TEXT_ASCENT_RATIO * fontSize);
  }

  const anchor = readAttrOrStyle(el, "text-anchor");
  const styles: string[] = [
    "position:absolute",
    `left:${fmt(left)}px`,
    `top:${fmt(top)}px`,
    "width:max-content",
    "white-space:nowrap",
  ];
  if (anchor === "middle") {
    styles.push("transform:translateX(-50%)", "text-align:center");
  } else if (anchor === "end") {
    styles.push("transform:translateX(-100%)", "text-align:right");
  }

  const fontFamily = readAttrOrStyle(el, "font-family");
  if (fontFamily) styles.push(`font-family:${fontFamily}`);
  styles.push(`font-size:${fmt(fontSize)}px`);
  const weight = readAttrOrStyle(el, "font-weight");
  if (weight) styles.push(`font-weight:${weight}`);
  if (readAttrOrStyle(el, "font-style") === "italic") {
    styles.push("font-style:italic");
  }
  const decoration = readAttrOrStyle(el, "text-decoration");
  if (decoration?.split(/\s+/).includes("underline")) {
    styles.push("text-decoration:underline");
  }
  styles.push(`color:${readAttrOrStyle(el, "fill") ?? "#000000"}`);
  styles.push(`line-height:${fmt(lineHeight)}px`);
  const letterSpacing = readAttrOrStyle(el, "letter-spacing");
  if (letterSpacing && letterSpacing !== "normal") {
    // SVG allows a bare number (user units = px); CSS needs the unit.
    styles.push(
      /^-?\d+(\.\d+)?$/.test(letterSpacing.trim())
        ? `letter-spacing:${letterSpacing.trim()}px`
        : `letter-spacing:${letterSpacing}`,
    );
  }
  const stroke = readAttrOrStyle(el, "stroke");
  if (stroke && stroke !== "none") {
    const strokeWidth = readAttrOrStyle(el, "stroke-width");
    styles.push(
      `-webkit-text-stroke:${fmt(strokeWidth ? num(strokeWidth) : 1)}px ${stroke}`,
      // Painting stroke under fill matches how designers use SVG text
      // outlines; CSS default paints it over and thins the glyphs.
      "paint-order:stroke",
    );
  }
  const opacity =
    parseOpacity(readAttrOrStyle(el, "opacity") ?? "1") *
    parseOpacity(readAttrOrStyle(el, "fill-opacity") ?? "1");
  if (opacity < 1) styles.push(`opacity:${fmt(opacity)}`);
  // Trailing `;` already, so it joins as its own declaration.
  const filterDecl = elementFilterCss(el, ctx);
  if (filterDecl) styles.push(filterDecl.replace(/;$/, ""));

  const content = shape.lines.map((line) => line.html).join("<br>");
  // Style values like font-family can carry quotes; keep the attribute valid.
  return `<div style="${escapeAttribute(`${styles.join(";")};`)}">${content}</div>`;
}

/** Multiple whitespace-separated (or comma-separated) numbers in a coordinate list. */
function isNumberList(value: string | undefined): boolean {
  if (!value) return false;
  return value.trim().split(/[\s,]+/).length > 1;
}

function textNodeData(node: DomNode): string {
  return (node as { data?: string }).data ?? "";
}

/** XML default whitespace handling: newlines and runs collapse to one space. */
function collapseWhitespace(value: string): string {
  return value.replace(/\s+/g, " ");
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function readPx(style: string, prop: string): number | null {
  const re = new RegExp(
    `(?:^|;)\\s*${prop}\\s*:\\s*(-?\\d+(?:\\.\\d+)?)\\s*(?:px)?\\s*(?:;|$)`,
    "i",
  );
  const m = re.exec(style);
  return m ? Number(m[1]) : null;
}

function styleBox(el: DomElement, ctx: ConvertContext, extras = ""): string {
  // Pure-translate per-element transforms fold cleanly into left/top, keeping
  // the layer tree flat. Non-translate transforms (rotate/scale/matrix) are
  // emitted as CSS `transform` via `elementTransformCss` and bypass this fold.
  const t = pureTranslateOf(el.attribs.transform);
  return (
    `position:absolute;` +
    `left:${fmt(num(el.attribs.x, ctx.viewportW) + ctx.offsetX + t.x)}px;` +
    `top:${fmt(num(el.attribs.y, ctx.viewportH) + ctx.offsetY + t.y)}px;` +
    `width:${fmt(num(el.attribs.width, ctx.viewportW))}px;` +
    `height:${fmt(num(el.attribs.height, ctx.viewportH))}px;` +
    extras
  );
}

function pureTranslateOf(transform: string | undefined): {
  x: number;
  y: number;
} {
  if (!transform || !isPureTranslate(transform)) return { x: 0, y: 0 };
  return parseTranslate(transform);
}

function readViewport(svg: DomElement): { width: number; height: number } {
  const w = num(svg.attribs.width);
  const h = num(svg.attribs.height);
  if (w > 0 && h > 0) return { width: w, height: h };
  const parts = (svg.attribs.viewBox ?? "").trim().split(/\s+/);
  return {
    width: w > 0 ? w : num(parts[2]) || DEFAULT_WIDTH,
    height: h > 0 ? h : num(parts[3]) || DEFAULT_HEIGHT,
  };
}

function parseTranslate(value: string | undefined): { x: number; y: number } {
  if (!value) return { x: 0, y: 0 };
  // Sum every translate(...) in the string — `transform="translate(10,10)
  // translate(5,5)"` should compose. Other transform functions are ignored
  // here; they're handled via the CSS-transform branch in walkGroup.
  let x = 0;
  let y = 0;
  const re = new RegExp(TRANSLATE_PATTERN.source, "g");
  let m: RegExpExecArray | null = re.exec(value);
  while (m !== null) {
    x += num(m[1]);
    y += num(m[2]);
    m = re.exec(value);
  }
  return { x, y };
}

function isPureTranslate(value: string): boolean {
  return TRANSLATE_ONLY_PATTERN.test(value);
}

const TRANSFORM_OP_PATTERN = /([a-zA-Z]+)\(([^)]*)\)/g;

/**
 * Translate an SVG `transform` attribute into a CSS `transform` value, or
 * null when it can't be expressed faithfully (callers record `skipped` so the
 * scene stays read-only rather than saving a silently mistranslated pose).
 *
 * The function names line up (translate / rotate / scale / matrix / skew*),
 * but the conventions differ: SVG `translate` takes user units (mapped to
 * px), SVG angles are unitless degrees while CSS requires a unit, SVG allows
 * space-separated arguments where CSS requires commas, and SVG `rotate` has a
 * three-argument rotate-about-a-point form CSS lacks (decomposed into
 * translate–rotate–translate).
 */
function svgTransformToCss(value: string): string | null {
  // Anything outside `name(args)` groups means an op we failed to match.
  if (value.replace(TRANSFORM_OP_PATTERN, "").trim() !== "") return null;

  const out: string[] = [];
  for (const match of value.matchAll(TRANSFORM_OP_PATTERN)) {
    const name = match[1]!;
    const args = match[2]!
      .trim()
      .split(/[\s,]+/)
      .filter(Boolean)
      .map(Number);
    if (args.some((n) => !Number.isFinite(n))) return null;
    const [a = 0, b = 0, c = 0] = args;
    if (name === "translate" && (args.length === 1 || args.length === 2)) {
      out.push(`translate(${fmt(a)}px, ${fmt(b)}px)`);
    } else if (name === "rotate" && args.length === 1) {
      out.push(`rotate(${fmt(a)}deg)`);
    } else if (name === "rotate" && args.length === 3) {
      out.push(
        `translate(${fmt(b)}px, ${fmt(c)}px) rotate(${fmt(a)}deg) ` +
          `translate(${fmt(-b)}px, ${fmt(-c)}px)`,
      );
    } else if (name === "scale" && args.length === 1) {
      out.push(`scale(${fmt(a)})`);
    } else if (name === "scale" && args.length === 2) {
      out.push(`scale(${fmt(a)}, ${fmt(b)})`);
    } else if ((name === "skewX" || name === "skewY") && args.length === 1) {
      out.push(`${name}(${fmt(a)}deg)`);
    } else if (name === "matrix" && args.length === 6) {
      out.push(`matrix(${args.map((n) => fmt(n)).join(", ")})`);
    } else {
      return null;
    }
  }
  return out.join(" ");
}

/**
 * Walk the entire SVG tree and index every element with an `id` attribute so
 * `<use href="#id">` can resolve forward and backward references. Excluding
 * descendants of `<defs>` would miss the common pattern of putting reusable
 * shapes inside `<defs>`, so we don't filter by ancestor.
 */
function collectIds(root: DomElement): Map<string, DomElement> {
  const map = new Map<string, DomElement>();
  const stack: DomNode[] = [root];
  while (stack.length > 0) {
    const node = stack.pop();
    if (!node || !isElement(node)) continue;
    const id = node.attribs.id;
    if (id && !map.has(id)) map.set(id, node);
    for (const child of node.children) stack.push(child);
  }
  return map;
}

function resolveFill(
  fill: string,
  gradients: Map<string, string>,
): string | undefined {
  if (!fill || fill === "none") return undefined;
  const urlMatch = /^url\(#([^)]+)\)$/.exec(fill);
  if (urlMatch) {
    return gradients.get(urlMatch[1]!);
  }
  return fill;
}

function collectGradients(
  $: CheerioAPI,
  idMap: Map<string, DomElement>,
  skipped: Set<string>,
): Map<string, string> {
  const gradients = new Map<string, string>();
  $("linearGradient, radialGradient").each((_i: number, el: any) => {
    const id: string | undefined = el.attribs?.id;
    if (!id) return;
    const stops = readStopsWithInheritance(el, idMap, 0);
    if (stops.length === 0) return;

    // `gradientUnits="userSpaceOnUse"` interprets x1/x2/cx/cy/r in absolute
    // canvas coords rather than relative to the filled shape's bounding box.
    // CSS gradients are always relative to the element being painted, so we
    // can't replicate userSpaceOnUse precisely. Warn rather than mis-rotate.
    const units = el.attribs.gradientUnits;
    if (units && units !== "objectBoundingBox") {
      skipped.add("gradientUnits");
    }

    // `gradientTransform` can be any affine transform. Only a single rotate
    // composes cleanly with our angle calc; everything else (scale, skew,
    // matrix) we record and leave to the user to inspect.
    const transformAttr = el.attribs.gradientTransform;
    const extraAngle = readSimpleRotate(transformAttr);
    if (transformAttr && extraAngle === null) {
      skipped.add("gradientTransform");
    }

    if (el.name === "linearGradient") {
      const baseAngle = computeLinearAngle(el.attribs);
      const angle = normalizeAngle(baseAngle + (extraAngle ?? 0));
      gradients.set(
        id,
        `linear-gradient(${fmt(angle)}deg, ${formatStops(stops)})`,
      );
    } else {
      // Radial gradient center. CSS defaults to "circle at 50% 50%"; emit
      // `at <cx>% <cy>%` only when the source departs from that default.
      // Explicit `r`, `fx`, `fy` don't map cleanly to CSS percentage radii or
      // focal points — leave them at the default (farthest-corner sizing)
      // and record the divergence.
      const radius =
        el.attribs.r !== undefined ? parsePercentOrFraction(el.attribs.r) : 50;
      if (
        radius !== 50 ||
        el.attribs.fx !== undefined ||
        el.attribs.fy !== undefined
      ) {
        skipped.add("radialGradient");
      }
      const cx =
        "cx" in el.attribs ? parsePercentOrFraction(el.attribs.cx) : 50;
      const cy =
        "cy" in el.attribs ? parsePercentOrFraction(el.attribs.cy) : 50;
      const position =
        cx !== 50 || cy !== 50 ? ` at ${fmt(cx)}% ${fmt(cy)}%` : "";
      gradients.set(
        id,
        `radial-gradient(circle${position}, ${formatStops(stops)})`,
      );
    }
  });
  return gradients;
}

/**
 * Resolve stop inheritance via `xlink:href` / `href`: SVG allows a gradient
 * with no `<stop>` children to point at another gradient and inherit its
 * stops. We follow the chain up to a small depth to guard against malformed
 * SVG cycles.
 */
function readStopsWithInheritance(
  grad: DomElement,
  idMap: Map<string, DomElement>,
  depth: number,
): GradientStop[] {
  const local = readStops(grad);
  if (local.length > 0) return local;
  if (depth >= MAX_USE_DEPTH) return [];
  const href = grad.attribs.href ?? grad.attribs["xlink:href"];
  if (!href || !href.startsWith("#")) return [];
  const target = idMap.get(href.slice(1));
  if (
    !target ||
    (target.name !== "linearGradient" && target.name !== "radialGradient")
  ) {
    return [];
  }
  return readStopsWithInheritance(target, idMap, depth + 1);
}

function readStops(grad: DomElement): GradientStop[] {
  const stops: GradientStop[] = [];
  for (const child of grad.children ?? []) {
    if (!isElement(child) || child.name !== "stop") continue;
    const offset = parsePercentOrFraction(child.attribs.offset);
    const style = child.attribs.style ?? "";
    const color =
      child.attribs["stop-color"] ?? readStyleProp(style, "stop-color");
    if (!color) continue;
    const opacityRaw =
      child.attribs["stop-opacity"] ?? readStyleProp(style, "stop-opacity");
    stops.push({ offset, color: combineColorWithOpacity(color, opacityRaw) });
  }
  return stops;
}

/**
 * SVG `*-opacity` (stop-opacity, fill-opacity, stroke-opacity) multiplies
 * into the color it modifies. CSS has no equivalent per-stop opacity inside
 * `linear-gradient(...)`, and `background:` doesn't accept a separate fill
 * alpha, so fold the alpha into the color itself.
 *
 * Prefer `rgba(r, g, b, a)` when the color is hex or rgb/rgba — the editor's
 * gradient parser (`packages/app/src/pages/ad-studio/html-ad-editor/parse.ts`)
 * recognizes those forms but bails on `color-mix(...)`, which would collapse
 * stops to `#000` after a parse→render round-trip. Fall back to `color-mix`
 * for formats we can't parse numerically (named colors, hsl, etc.).
 */
/**
 * Match a `gradientTransform` string of the form `rotate(N)` and return N.
 * Returns null for absent, unparseable, or non-rotate transforms — those go
 * to the `skipped` warning set instead of silently mis-rotating the gradient.
 */
function readSimpleRotate(transform: string | undefined): number | null {
  if (!transform) return null;
  const m = /^\s*rotate\(\s*(-?[\d.]+)\s*\)\s*$/.exec(transform);
  return m ? num(m[1]) : null;
}

function normalizeAngle(deg: number): number {
  return ((deg % 360) + 360) % 360;
}

function parsePercentOrFraction(value: string | undefined): number {
  if (!value) return 0;
  const trimmed = value.trim();
  if (trimmed.endsWith("%")) return num(trimmed.slice(0, -1));
  const fraction = num(trimmed);
  return fraction <= 1 ? fraction * 100 : fraction;
}

/** `visibility="hidden"` / `"collapse"` and `display="none"` hide the subtree. */
function isHidden(el: DomElement): boolean {
  const visibility = readAttrOrStyle(el, "visibility");
  if (visibility === "hidden" || visibility === "collapse") return true;
  const display = readAttrOrStyle(el, "display");
  if (display === "none") return true;
  return false;
}

/**
 * A `mask` reference is a soft-masking effect we don't paint. Recording it
 * lets callers surface a "this ad uses effects that won't render in the
 * editor" warning instead of silently producing different visuals.
 *
 * `filter` used to be recorded here alongside it, unconditionally. It isn't
 * any more: most filters have an exact CSS equivalent, so each emitter asks
 * {@link filterCssFor} for a declaration and only the ones that come back
 * empty-handed are recorded.
 */
function recordMaskWarning(el: DomElement, ctx: ConvertContext): void {
  if (el.attribs.mask !== undefined) ctx.skipped.add("mask");
}

/** The CSS `filter` declaration for an element, recording it when it's lossy. */
function elementFilterCss(el: DomElement, ctx: ConvertContext): string {
  return filterCssFor(el, ctx.filters, ctx.skipped);
}

/** Build an `opacity:X;` CSS declaration when the element has one, else "". */
function opacityCss(el: DomElement): string {
  const opacity = readAttrOrStyle(el, "opacity");
  if (opacity === undefined || opacity === "") return "";
  return `opacity:${fmt(parseOpacity(opacity))};`;
}

/** Opacity accepts a bare number or a percentage; malformed values paint opaque. */
function parseOpacity(value: string): number {
  const trimmed = value.trim();
  const n = trimmed.endsWith("%")
    ? Number(trimmed.slice(0, -1)) / 100
    : Number(trimmed);
  return Number.isFinite(n) ? Math.min(1, Math.max(0, n)) : 1;
}

/**
 * CSS `transform` declaration for a leaf element's own `transform` attribute,
 * excluding pure-translate (which `styleBox` folds into left/top to keep the
 * layer flat). SVG transforms act about the local user-space origin, not the
 * element box: the box corner sits at local `(x, y)`, so the CSS
 * `transform-origin` is `(-x, -y)` relative to the box (degenerating to
 * `0 0` for elements at the origin). Untranslatable transforms are recorded
 * in `skipped` and dropped — the read-only gate keeps the mistranslation
 * from being saved.
 */
function elementTransformCss(el: DomElement, ctx: ConvertContext): string {
  const transform = el.attribs.transform;
  if (!transform || isPureTranslate(transform)) return "";
  const css = svgTransformToCss(transform);
  if (css === null) {
    ctx.skipped.add("transform");
    return "";
  }
  const originX = -num(el.attribs.x, ctx.viewportW);
  const originY = -num(el.attribs.y, ctx.viewportH);
  return `transform:${css};transform-origin:${fmt(originX)}px ${fmt(originY)}px;`;
}

function formatStops(stops: GradientStop[]): string {
  return stops.map((s) => `${s.color} ${fmt(s.offset)}%`).join(", ");
}

/**
 * Convert an SVG linearGradient's `(x1, y1) → (x2, y2)` vector into a CSS
 * gradient angle. CSS gradients are measured clockwise from "to top" (0deg);
 * `to bottom` is 180deg, `to right` is 90deg. SVG defaults: x1=0, y1=0,
 * x2=100%, y2=0 (left-to-right).
 */
function computeLinearAngle(attribs: Record<string, string>): number {
  const x1 = parsePercentOrFraction(attribs.x1);
  const y1 = parsePercentOrFraction(attribs.y1);
  const x2 = "x2" in attribs ? parsePercentOrFraction(attribs.x2) : 100;
  const y2 = "y2" in attribs ? parsePercentOrFraction(attribs.y2) : 0;
  const dx = x2 - x1;
  const dy = y2 - y1;
  if (dx === 0 && dy === 0) return 180;
  const radians = Math.atan2(dx, -dy);
  const degrees = (radians * 180) / Math.PI;
  return ((degrees % 360) + 360) % 360;
}
